// ─── Community.js ─────────────────────────────────────────────
import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import API from '../utils/apiconfig';
import { useGlobalError } from '../context/GlobalErrorContext';
import TablePagination from '../components/TablePagination';
import { ScrollableListBody } from '../components/ScrollableTable';
import { DEFAULT_PAGE_SIZE, parsePaginatedResponse } from '../utils/pagination';
import AppButton from '../components/ui/AppButton';
import { formatAppDate, formatAppDateTime } from '../utils/dateFormat';

const TYPE_STYLE = {
  announcement: { color: 'bg-blue-50 text-blue-600 border-blue-200', icon: '📣' },
  event: { color: 'bg-violet-50 text-violet-600 border-violet-200', icon: '📅' },
  training: { color: 'bg-emerald-50 text-emerald-600 border-emerald-200', icon: '🎓' },
  news: { color: 'bg-amber-50 text-amber-600 border-amber-200', icon: '📰' },
};

export function Community() {
  const { reportError } = useGlobalError();
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const loadPosts = useCallback(async (nextPage = 1, filterOverride = null) => {
    setLoading(true);
    const typeFilter = filterOverride !== null ? filterOverride : filter;
    try {
      const params = { page: nextPage, page_size: pageSize };
      if (typeFilter !== 'all') params.type = typeFilter;
      const r = await api.get(API.COMMUNITY, { params });
      const parsed = parsePaginatedResponse(r.data, ['results']);
      setPosts(parsed.items);
      setTotalPages(parsed.totalPages);
      setTotalCount(parsed.count);
      setPage(parsed.page);
    } catch (err) {
      reportError(err);
      setPosts([]);
      setTotalPages(1);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [filter, pageSize, reportError]);

  const clearFilters = () => {
    setFilter('all');
    loadPosts(1, 'all');
  };

  // Initial + page-size only. Type chips apply via Apply Filters.
  useEffect(() => {
    loadPosts(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional Apply Filters pattern
  }, [pageSize]);

  return (
    <div className="min-h-screen bg-slate-50 py-10">
      <div className="max-w-3xl mx-auto px-6 space-y-5">
        <div className="border-b border-slate-200 pb-5 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-black text-slate-900" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>Community</h1>
            <p className="text-sm text-slate-400 mt-1">Announcements, events and training from SuccessFlow</p>
          </div>
        </div>

        <div className="flex gap-2 flex-wrap items-center">
          {['all', 'announcement', 'event', 'training', 'news'].map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors capitalize ${
                filter === f ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'
              }`}
            >
              {f === 'all' ? 'All Posts' : `${TYPE_STYLE[f]?.icon} ${f}`}
            </button>
          ))}
          <AppButton
            type="button"
            variant="primary"
            icon="search"
            disabled={loading}
            onClick={() => loadPosts(1)}
          >
            Apply Filters
          </AppButton>
          <AppButton
            type="button"
            variant="secondary"
            icon="x"
            disabled={loading}
            onClick={clearFilters}
          >
            Clear Filters
          </AppButton>
        </div>

        {loading && posts.length === 0 ? null : posts.length === 0 ? (
          <div className="text-center py-20 bg-white border border-slate-200 rounded-2xl">
            <div className="text-4xl mb-3">📣</div>
            <p className="text-slate-500 font-semibold">No posts yet</p>
            <p className="text-sm text-slate-400 mt-1">Admin posts will appear here.</p>
          </div>
        ) : (
          <>
            <ScrollableListBody className="space-y-4" listStripe={false}>
              {posts.map((post) => {
                const t = TYPE_STYLE[post.post_type] || TYPE_STYLE.announcement;
                return (
                  <div
                    key={post.id}
                    className={`bg-white border border-slate-200 rounded-2xl overflow-hidden ${post.is_pinned ? 'ring-1 ring-amber-300' : ''}`}
                  >
                    <div className="p-5">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          {post.is_pinned && (
                            <span className="text-[10px] font-black bg-amber-100 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full">📌 Pinned</span>
                          )}
                          <span className={`text-xs font-bold px-2.5 py-1 rounded-full border capitalize ${t.color}`}>
                            {t.icon} {post.post_type}
                          </span>
                        </div>
                        <span className="text-xs text-slate-400 flex-shrink-0">
                          {formatAppDate(post.created_at)}
                        </span>
                      </div>
                      <h2 className="text-base font-black text-slate-900 mb-2">{post.title}</h2>
                      <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{post.body}</p>
                      {post.event_date && (
                        <div className="mt-4 p-3 bg-violet-50 border border-violet-100 rounded-xl">
                          <p className="text-xs font-bold text-violet-700">
                            📅 Event Date: {formatAppDateTime(post.event_date)}
                          </p>
                        </div>
                      )}
                      <p className="text-xs text-slate-400 mt-3">Posted by {post.created_by_username}</p>
                    </div>
                  </div>
                );
              })}
            </ScrollableListBody>
            <TablePagination
              page={page}
              totalPages={totalPages}
              onPageChange={(p) => loadPosts(p)}
              pageSize={pageSize}
              onPageSizeChange={(next) => { setPageSize(next); setPage(1); }}
              disabled={loading}
              count={totalCount}
              rowsPerPageId="community-rows-per-page"
            />
          </>
        )}
      </div>
    </div>
  );
}

export default Community;
